import { ReactComponent as CheckBoxDefault } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckBoxFilled } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Checkbox,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import zIndex from '@mui/material/styles/zIndex';
import { cloneDeep } from "lodash";

// Component
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { MenuSelect } from "components/Select/menuSelect";
import ImportView from "./ImportView";

const macAddressCSVExample = [
  { mac: 'MAC Address', description: 'Description(Optional)', },
  { mac: '347C12FA894E', description: 'Office Printer', },
  { mac: '91BC334F22A1', description: 'Guest WiFi Tablet', },
  { mac: 'F01B5D9277C3', description: '', },
  { mac: '6B448E10CC5F', description: 'Conference Room AP', },
  { mac: '8D2A6F33B19E', description: 'Security Camera', },
  { mac: 'C49F21DE4872', description: 'POS Terminal', },
  { mac: 'E7550A6C901D', description: '', },
  { mac: '5C03BF8E2466', description: 'Lobby Display Screen', },
];

const defaultMacAddressFormatOption = {
  dash: {
    label: "XX-XX-XX-XX-XX-XX",
    value: "dash",
  },
  colon: {
    label: "XX:XX:XX:XX:XX:XX",
    value: "colon",
  },
  none: {
    label: "XXXXXXXXXXXX",
    value: "plain",
  },
};

const UploadView = ({
  viewStateEnum,
  getViewTitle,
  form,
  handleUploadView,
}) => {
  const theme = useTheme();
  const viewRef = useRef(null);

  // Status
  const [openImportView, setOpenImportView] = useState(false);
  const [macAddressFormatList, setMacAddressFormatList] = useState(cloneDeep(Object.values(defaultMacAddressFormatOption)));
  const [selectedMacAddressFormat, setSelectedMacAddressFormat] = useState(defaultMacAddressFormatOption.dash.value);
  const [macAddressItemList, setMacAddressItemList] = useState([]);
  const [replaceFlag, setReplaceFlag] = useState(false);

  // Method
  function HandleImportView() {
    return {
      closeView: () => {
        setOpenImportView(false);
      },
      updateMacAddressItemsList: (items) => {
        const updatedMacAddressItemList = [];
        items.forEach(item => {
          updatedMacAddressItemList.push({
            macAddress: item.mac,
            description: item.description,
          });
        });
        setMacAddressItemList(updatedMacAddressItemList);
      }
    }
  }

  const downloadCSVExample = () => {
    const copyData = cloneDeep(macAddressCSVExample);

    copyData.forEach((item, index) => {
      if (index === 0) return; // Skip header row

      let formattedMac = item.mac;
      if (selectedMacAddressFormat === defaultMacAddressFormatOption.dash.value) {
        formattedMac = item.mac.match(/.{1,2}/g).join('-');
      } else if (selectedMacAddressFormat === defaultMacAddressFormatOption.colon.value) {
        formattedMac = item.mac.match(/.{1,2}/g).join(':');
      }

      item.mac = formattedMac;
    });

    const csvContent = "data:text/csv;charset=utf-8,"
      + copyData.map(e => `${e.mac},${e.description}`).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "mac_address_sample.csv");
    document.body.appendChild(link);

    link.click();
    document.body.removeChild(link);
  }

  const closeView = () => {
    handleUploadView.closeView();
  }

  const displayImportView = () => {
    setOpenImportView(true);
  }

  const checkFormValid = () => {
    if (macAddressItemList.length > 0) {
      return true;
    }

    return false;
  }

  const handleSave = () => {
    if (!checkFormValid()) return;

    handleUploadView.updateMacAddressItemsList(replaceFlag, macAddressItemList);
    closeView();
  }

  // Side Effects
  useEffect(() => {
    setMacAddressItemList([]);
    // setMacAddressItemList([
    //   ...Array.from({ length: 100 }, () => cloneDeep({
    //     macAddress: "AA:BB:CC:DD:EE:FF",
    //     description: "Sample Description",
    //   }))
    // ]);
    setSelectedMacAddressFormat(defaultMacAddressFormatOption.dash.value);
  }, []);

  let viewHeight = 0;
  let marginTop = 0;
  if (viewRef && viewRef.current) {
    viewHeight = viewRef.current.offsetHeight;
    marginTop = viewHeight / 6;
  }
  const parentViewWidth = viewRef && viewRef.current ? viewRef.current.offsetWidth : 0;
  const parentViewHeight = viewRef && viewRef.current ? viewRef.current.offsetHeight : 0;

  return (
    <>
      <Box
        ref={viewRef}
        style={{
          position: "relative",
          padding: '0',
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              paddingLeft: "20px",
            }}
          >
            {getViewTitle(viewStateEnum.UPLOAD)}
          </Typography>
          <Button
            onClick={closeView}
            sx={{
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
            marginLeft: '10px',
            marginRight: '10px',
            marginBottom: '10px',
          }}
        >
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '10px',
              paddingLeft: '10px',
            }}
          >
            <Box sx={form.getLabelStyle()}>MAC Address Format:</Box>
            <MenuSelect
              value={selectedMacAddressFormat}
              onChange={(opt) => {
                setSelectedMacAddressFormat(opt.value);
              }}
              options={macAddressFormatList}
              width="200px"
              height="32px"
            />
          </Box>
          <OutlineRectangleButton
            startIcon={<AddIcon />}
            height="32px"
            onClick={downloadCSVExample}
          >
            Download CSV Sample
          </OutlineRectangleButton>
        </Box>

        <Box
          style={{
            border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
            borderRadius: '4px',
            margin: '0 10px 0 20px',
          }}
        >
          <Box sx={{ ...form.getTableStyle(), height: '300px', padding: '0 10px' }}>
            <TableContainer
              component={Paper}
              sx={{
                "&.MuiPaper-root": {
                  boxShadow: "none",
                  border: 'none',
                },
              }}
              style={{ overflowY: 'auto' }}
            >
              <Table
                sx={{
                  borderCollapse: "collapse",
                  "& td, & th": {
                    border: "none",
                  },
                  "& tbody tr:nth-of-type(odd)": {
                    backgroundColor: 'rgb(246, 246, 246)',
                  },
                  "& tbody tr:nth-of-type(even)": {
                    backgroundColor: theme.palette.primary.white,
                  },
                }}
              >
                <TableHead>
                  <TableRow style={{ borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}` }}>
                    <TableCell sx={{ fontWeight: 600 }}>MAC Address</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description(Optional)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    macAddressItemList.map((mac, index) => (
                      <>
                        <TableRow key={index}>
                          <TableCell>
                            {mac.macAddress}
                          </TableCell>
                          <TableCell>
                            {mac.description}
                          </TableCell>
                        </TableRow>
                      </>
                    ))
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
            marginLeft: '20px',
            marginRight: '10px',
            marginBottom: '20px',
          }}
        >
          <Box sx={form.getLabelStyle()}>
            MAC Address: {macAddressItemList.length}
          </Box>
          <OutlineRectangleButton
            startIcon={<AddIcon />}
            height="32px"
            onClick={displayImportView}
          >
            Upload
          </OutlineRectangleButton>
        </Box>

        <Box style={{ marginLeft: '10px', marginBottom: '10px' }}>
          <Checkbox
            icon={
              <CheckBoxDefault
                style={{ width: "24px", height: "24px" }}
              />
            }
            checkedIcon={
              <CheckBoxFilled
                style={{ width: "24px", height: "24px" }}
              />
            }
            checked={replaceFlag}
            onChange={(e) => setReplaceFlag(e.target.checked)}
          />
          <Typography
            component="span"
            sx={{
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[0],
            }}
          >
            Replace the existing MAC address in the list.
          </Typography>
        </Box>

        <Box style={{ marginLeft: '20px', marginBottom: '30px' }}>
          <Box>
            <Typography
              component="span"
              sx={{
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[0],
              }}
            >
              Note:
            </Typography>
          </Box>
          <Box>
            <Typography
              component="span"
              sx={{
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[0],
              }}
            >
              1. Each MAC address and its description on a separate line, with a space between them.
            </Typography>
          </Box>
        </Box >

        <Box
          sx={{
            margin: "24px 0 16px auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            onClick={closeView}
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
          >
            Cancel
          </OutlineButton>
          <SolidButton
            onClick={handleSave}
            width="100px"
            height="32px"
            disabled={!checkFormValid()}
          >
            Import
          </SolidButton>
        </Box>

        {
          openImportView && (
            <Box style={{
              ...form.getMaskStyle(),
              width: parentViewWidth + 40 + 'px',
              height: parentViewHeight + 60 + 'px',
              transform: `translateY(-20px) translateX(-20px)`,
            }} />
          )
        }
      </Box>

      {
        openImportView && (
          <>
            <Box
              style={{
                ...form.getInnerViewStyle(zIndex),
                display: 'flex',
                alignItems: 'center',
                width: parentViewWidth - 40 + 'px',
                transform: `translateY(${marginTop}px) translateX(40px)`,
                marginTop: marginTop,
              }}
            >
              <Box style={form.getInnerContainerStyle()}>
                <ImportView
                  viewStateEnum={viewStateEnum}
                  getViewTitle={getViewTitle}
                  form={form}
                  handleImportView={HandleImportView()}
                  macAddressFormat={selectedMacAddressFormat}
                />
              </Box>
            </Box>
          </>
        )
      }
    </>
  )
}

export default React.memo(UploadView);